/**
 * controls.jsx - the small controls of the Customize panel (round 4,
 * 10/9/26): a switch, a segmented choice, a colour choice (presets, none,
 * any colour from the picker), a section header with its own Reset, and a
 * hint line. Keyboard friendly: real buttons and inputs, aria states set.
 */
import { HIGHLIGHT_COLORS } from '../highlights';

const HEX = /^#[0-9a-fA-F]{6}$/;
const ring = (on) => ({ borderColor: on ? '#1B5E20' : '#D0D0D0', boxShadow: on ? '0 0 0 2px #1B5E20' : 'none' });

/** An on / off switch. `label` names it for screen readers (and tests). */
export function Switch({ on, onChange, label, disabled = false, small = false }) {
  const w = small ? 28 : 34;
  const h = small ? 16 : 20;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className="relative shrink-0 rounded-full disabled:opacity-40"
      style={{ width: w, height: h, background: on ? '#2E7D32' : '#C9CFC4', transition: 'background-color 150ms ease-out' }}
    >
      <span
        aria-hidden="true"
        className="absolute top-[2px] rounded-full bg-white shadow"
        style={{ width: h - 4, height: h - 4, left: on ? w - h + 2 : 2, transition: 'left 150ms ease-out' }}
      />
    </button>
  );
}

/** One of a few options, side by side. options: [[value, label]]. */
export function Segmented({ value, onChange, options, label }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border border-border-strong overflow-hidden bg-surface">
      {options.map(([v, text], i) => (
        <button
          key={String(v)}
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(v)}
          className={`px-2.5 py-1 text-[11.5px] font-semibold ${i ? 'border-l border-border-strong' : ''} ${value === v ? 'bg-gat-green text-white' : 'text-text-body hover:bg-surface-2'}`}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/** None (optional), the presets, and any colour from the native picker. value: a preset key, '#RRGGBB' or 'none'. */
export function ColorChoice({ label, presets = HIGHLIGHT_COLORS, value, onChange, text = false, allowNone = true }) {
  const custom = HEX.test(value || '');
  return (
    <div className="flex items-center gap-1.5 flex-wrap" role="radiogroup" aria-label={label}>
      <span className="min-w-[64px] text-[11.5px] text-text-body">{label.replace(/^Highlight /, '')}</span>
      {allowNone && (
        <button
          type="button"
          role="radio"
          aria-checked={value === 'none'}
          aria-label="none"
          title="none"
          onClick={() => onChange('none')}
          className="w-6 h-6 rounded border text-[10px] text-text-muted bg-surface"
          style={ring(value === 'none')}
        >
          ∅
        </button>
      )}
      {Object.entries(presets).map(([key, c]) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={value === key}
          aria-label={c.label}
          title={c.label}
          onClick={() => onChange(key)}
          className="w-6 h-6 rounded border text-[11px] font-bold"
          style={{ ...(text ? { color: c.fill, background: '#fff' } : { background: c.fill }), ...ring(value === key) }}
        >
          {text ? 'A' : ''}
        </button>
      ))}
      <label className="inline-flex items-center gap-1 ml-1 cursor-pointer" title="Any colour">
        <input
          type="color"
          aria-label={`${label} - custom`}
          value={custom ? value.toLowerCase() : text ? '#b91c1c' : '#fef08a'}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="w-7 h-6 p-0 border rounded cursor-pointer"
          style={ring(custom)}
        />
        <span className="text-text-muted text-[11px]">{custom ? value.toUpperCase() : 'custom'}</span>
      </label>
    </div>
  );
}

/** A section's title, its plain-language hint, and its own Reset. */
export function SectionHead({ title, hint, onReset, resetLabel }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-3">
      <div>
        <h4 className="m-0 text-[13px] font-bold text-text-dark">{title}</h4>
        {hint && <p className="m-0 mt-0.5 text-[11.5px] text-text-muted leading-snug">{hint}</p>}
      </div>
      {onReset && (
        <button type="button" onClick={onReset} className="shrink-0 text-[11.5px] font-semibold text-gat-greenDark hover:underline">
          {resetLabel || 'Reset'}
        </button>
      )}
    </div>
  );
}

/** A labelled row: the words on the left, the control on the right. */
export function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-b-0">
      <div className="min-w-0">
        <div className="text-[12px] font-semibold text-text-dark">{label}</div>
        {hint && <div className="text-[11px] text-text-muted leading-snug">{hint}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}
